import { Button } from '@/components/ui/button';
import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/privacy-policy')({
    component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
    return (
        <div className="min-h-screen bg-emerald-50 text-emerald-950">
            <header className="border-b border-emerald-100 bg-white/80">
                <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-5">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src="https://res.cloudinary.com/flannery38/image/upload/v1771973120/stockwright/logo.svg"
                            alt="Stockwright"
                            className="h-6"
                        />
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/faq"
                            className="text-sm text-emerald-800/80 hover:text-emerald-900"
                        >
                            FAQ
                        </Link>
                        <Link
                            to="/security"
                            className="text-sm text-emerald-800/80 hover:text-emerald-900"
                        >
                            Security
                        </Link>
                        <Button asChild className="bg-emerald-700 text-white">
                            <Link to="/auth" search={{ mode: 'signup' }}>
                                Start Free Trial
                            </Link>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto w-full max-w-5xl px-4 py-12">
                <div className="mb-10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600/80">
                        Privacy
                    </p>
                    <h1 className="mt-2 text-3xl font-semibold">
                        Privacy Policy
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm text-emerald-900/70">
                        Effective June 7, 2025. Stockwright ("we", "us", "our")
                        is operated by Lobby LLC. This policy explains what data
                        we collect, how we use it, and your rights around it. By
                        using Stockwright, you agree to this policy.
                    </p>
                </div>

                <div className="grid gap-6">
                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">
                            1. Information We Collect
                        </h2>
                        <ul className="mt-2 space-y-1 text-sm text-emerald-900/70 list-disc list-inside">
                            <li>
                                <strong>Account information:</strong> name and
                                email address provided at registration
                            </li>
                            <li>
                                <strong>Inventory and sales data:</strong>{' '}
                                items, costs, sale prices, platforms, and profit
                                records you enter into the product
                            </li>
                            <li>
                                <strong>Usage data:</strong> basic logs of how
                                the app is used for debugging and improving the
                                service
                            </li>
                            <li>
                                <strong>Payment information:</strong> billing is
                                handled by Stripe — we do not store your card
                                details directly
                            </li>
                        </ul>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">
                            2. How We Use Your Information
                        </h2>
                        <ul className="mt-2 space-y-1 text-sm text-emerald-900/70 list-disc list-inside">
                            <li>To create and manage your account</li>
                            <li>
                                To provide the Stockwright service and sync data
                                across the app and Chrome extension
                            </li>
                            <li>
                                To send transactional emails (account
                                verification, password reset)
                            </li>
                            <li>To improve and debug the product</li>
                            <li>
                                We do not sell your data. We do not use your
                                data for advertising.
                            </li>
                        </ul>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">
                            3. Data Storage and Security
                        </h2>
                        <p className="mt-2 text-sm text-emerald-900/70">
                            Your data is stored on secured, privately hosted
                            servers. We use industry-standard practices to
                            protect your data from unauthorized access. Payment
                            processing is handled by Stripe, a PCI-compliant
                            third party.
                        </p>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">
                            4. Chrome Extension
                        </h2>
                        <p className="mt-2 text-sm text-emerald-900/70">
                            The Stockwright Chrome extension accesses data on
                            supported resale platform pages (e.g. eBay, Amazon)
                            solely to assist with inventory tracking features
                            you initiate. The extension does not collect
                            browsing history or activity outside of supported
                            Stockwright features. Data captured through the
                            extension is transmitted securely to your
                            Stockwright account.
                        </p>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">
                            5. Data Sharing
                        </h2>
                        <p className="mt-2 text-sm text-emerald-900/70">
                            We do not sell or rent your personal data. We share
                            data only with third-party services required to
                            operate Stockwright (e.g. payment processor, email
                            delivery). We may disclose data if required by law.
                        </p>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">
                            6. Data Retention
                        </h2>
                        <p className="mt-2 text-sm text-emerald-900/70">
                            Your data is retained as long as your account is
                            active. Upon account deletion, your data is removed
                            from our systems within 30 days.
                        </p>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">
                            7. Your Rights
                        </h2>
                        <p className="mt-2 text-sm text-emerald-900/70">
                            You can export or delete your data by contacting us
                            or through account settings. You can cancel your
                            account at any time.
                        </p>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">8. Cookies</h2>
                        <p className="mt-2 text-sm text-emerald-900/70">
                            Stockwright uses cookies and local storage to
                            maintain your session and remember preferences. We
                            do not use third-party tracking cookies.
                        </p>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">
                            9. Changes to This Policy
                        </h2>
                        <p className="mt-2 text-sm text-emerald-900/70">
                            We may update this policy and will notify you by
                            updating the effective date at the top of this page.
                            Continued use of the service after changes
                            constitutes acceptance.
                        </p>
                    </section>

                    <section className="rounded-2xl border border-emerald-100 bg-white p-6">
                        <h2 className="text-lg font-semibold">10. Contact</h2>
                        <p className="mt-2 text-sm text-emerald-900/70">
                            For privacy questions or data requests, contact us
                            at{' '}
                            <a
                                href="mailto:privacy@stockwright.co"
                                className="text-emerald-700 underline"
                            >
                                privacy@stockwright.co
                            </a>
                            .
                        </p>
                    </section>
                </div>
            </main>
        </div>
    );
}
